import { useEffect, useState } from 'react'
import { Drawer, Field } from '@/kit/ui'
import { endpointLabel, testsPathOf } from '@/lib/endpoints'
import { KNOWN_OUTCOMES } from '@/lib/outputCheck'
import type { TestEvent } from '@/types'

const STANDARD: readonly string[] = KNOWN_OUTCOMES

/** Add a test event (Lambda's "Configure test event"): a sample lender reply for one endpoint and the outcome its handler must return. */
export function TestEventDrawer({ isOpen, onClose, endpoint, outcomes, onAdd, initial }: {
  isOpen: boolean
  onClose: () => void
  endpoint: string
  outcomes: string[]
  onAdd: (t: Omit<TestEvent, 'id'>) => void
  /** filled in from a real reply (the API client's "Make a test event") */
  initial?: { name?: string; status: number; body: unknown; expectedOutcome?: string | null } | null
}) {
  const [name, setName] = useState('')
  const [expected, setExpected] = useState('ok')
  const [status, setStatus] = useState('200')
  const [body, setBody] = useState('{\n  \n}')
  const [error, setError] = useState<string>()
  useEffect(() => {
    if (isOpen) {
      setName(initial?.name ?? '')
      setExpected(initial?.expectedOutcome && STANDARD.includes(initial.expectedOutcome) ? initial.expectedOutcome : outcomes[0] ?? 'ok')
      setStatus(initial ? String(initial.status) : '200')
      setBody(initial ? JSON.stringify(initial.body ?? null, null, 2) : '{\n  \n}')
      setError(undefined)
    }
  }, [isOpen]) // eslint-disable-line react-hooks/exhaustive-deps

  const submit = () => {
    let parsed: unknown = null
    const trimmed = body.trim()
    if (trimmed && trimmed !== 'null') {
      try {
        parsed = JSON.parse(trimmed)
      } catch {
        return setError('The body must be valid JSON (or empty / null for no body).')
      }
    }
    const code = Number(status)
    if (!Number.isInteger(code) || code < 0 || code > 599) return setError('HTTP status: 0 (no reply) to 599.')
    if (name.trim().length < 2) return setError('Give the event a name, e.g. Wrong PAN.')
    if (!STANDARD.includes(expected)) return setError(`The outcome must be one the system knows: ${STANDARD.join(', ')}.`)
    onAdd({ endpoint, name: name.trim(), reply: { status: code, body: parsed }, expectedOutcome: expected, source: 'sample' })
    onClose()
  }

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="Add a test event"
      subtitle={`${endpointLabel(endpoint)} · a sample lender reply and the outcome the handler must return`}
      footer={
        <div className="flex justify-end gap-2">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={submit} data-testid="add-test-event">Add to the working copy</button>
        </div>
      }
    >
      <Field label="Name" required help="What the lender did, e.g. Wrong PAN">
        {(id) => <input id={id} className="input" value={name} onChange={(e) => setName(e.target.value)} />}
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Expected outcome" required help="One the system knows and the endpoint declares; it is an output port on the canvas">
          {(id) => (
            <>
              <input id={id} className="input mono" list={`${id}-list`} value={expected} onChange={(e) => setExpected(e.target.value)} />
              <datalist id={`${id}-list`}>{Array.from(new Set([...outcomes, ...STANDARD])).map((o) => <option key={o} value={o} />)}</datalist>
            </>
          )}
        </Field>
        <Field label="HTTP status" required help="0 = no reply (timeout)">
          {(id) => <input id={id} className="input mono" inputMode="numeric" value={status} onChange={(e) => setStatus(e.target.value)} />}
        </Field>
      </div>
      <Field label="Reply body (JSON)" help="Exactly as the lender sends it; mask real references">
        {(id) => <textarea id={id} className="input mono min-h-40" value={body} onChange={(e) => setBody(e.target.value)} spellCheck={false} />}
      </Field>
      {error && <p className="field-error">{error}</p>}
      <p className="mt-2 text-2xs text-muted">Added to the working copy; <b>Save</b> commits it as <code>{testsPathOf(endpoint)}</code>.</p>
    </Drawer>
  )
}
